import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#syncLayoutOptions(this.querySelectorAll(`input[name="${target.name}"]`), target.value);
    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const viewport = event.matches ? 'desktop' : 'mobile';
    const optionName = event.matches ? 'grid' : 'grid-mobile';
    const options = this.querySelectorAll(`input[name="${optionName}"]`);
    const savedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`) || 'default';
    const layout = [...options].some((option) => option.value === savedLayout) ? savedLayout : 'default';

    this.#syncLayoutOptions(options, layout);
    this.#setLayout(layout);
  };

  #syncLayoutOptions(options, layout) {
    options.forEach((option) => {
      option.checked = option.value === layout;
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
